import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useConversation } from '@elevenlabs/react';
import api from '../api';
import { UAParser } from 'ua-parser-js';
import {
  ElevenLabsConversationTokenRequest,
  ElevenLabsConversationTokenResponse,
  ElevenLabsConversationEventRequest,
  ElevenLabsConversationEventResponse,
  ElevenLabsEventType,
  ElevenLabsEventContent,
  WebRTCChatVisualElementsParams,
  WebRTCChatVisualElementsResponse,
} from '@dreamai/types';
import {
  useOpenAIRealtime,
  OpenAIRealtimeConfig,
} from '../hooks/useOpenAIRealtime';

import { ReactComponent as CallCompletedIcon } from '../icons/webrtc-calls/call-completed.svg';
import { ReactComponent as EndCallIcon } from '../icons/webrtc-calls/end-call.svg';
import { ReactComponent as MicrophoneConnectedIcon } from '../icons/webrtc-calls/microphone-connected.svg';
import { ReactComponent as NoMicrophoneIcon } from '../icons/webrtc-calls/no-microphone.svg';
import { ReactComponent as StartCallIcon } from '../icons/webrtc-calls/start-call-icon.svg';
import { ReactComponent as CallingIcon } from '../icons/webrtc-calls/calling.svg';
import { ReactComponent as CallFailedIcon } from '../icons/webrtc-calls/call-failed.svg';
import { ReactComponent as DreamLogo } from '../icons/dream-logo.svg';
import { ReactComponent as DiallerMicIcon } from '../icons/mic.svg';
import { ReactComponent as DiallerMicOffIcon } from '../icons/webrtc-calls/no-microphone.svg';
import { ReactComponent as DiallerCloseIcon } from '../icons/close-x.svg';
import { ReactComponent as DiallerAlertIcon } from '../icons/alert-square.svg';

import WhatsappIcon from '../icons/whatsapp.png';
import AudioReactiveCanvas from './AudioReactiveCanvas';

import './webrtc-voice-chat.scss';

// Device detection utility using ua-parser-js
const getDeviceType = (): 'mobile' | 'desktop' | 'tablet' => {
  const parser = new UAParser();
  const device = parser.getDevice();

  if (device.type === 'mobile') return 'mobile';
  if (device.type === 'tablet') return 'tablet';
  return 'desktop';
};

interface WebRTCVoiceChatProps {
  // Props mode - pass these to override URL params
  agentId?: string;
  threadId?: string;
  callId?: string;
  voiceId?: string;
  structuredCallId?: string;
  // UI mode props
  miniMode?: boolean;
  /** New avatar + dialler-pill UI (replaces miniMode in the chat input) */
  diallerMode?: boolean;
  autoStart?: boolean;
  // Control props
  shouldHangup?: boolean;
  // Agent first message - when provided, agent speaks this first (for agent-initiated calls)
  agentFirstMessage?: string;
  // Callback props
  onConnectionChange?: (connected: boolean) => void;
  onAudioLevelChange?: (level: number) => void;
  onOutputLevelChange?: (level: number) => void;
  onError?: (error: string) => void;
  onHangupComplete?: () => void;
}

const WebRTCVoiceChat: React.FC<WebRTCVoiceChatProps> = ({
  agentId,
  threadId,
  callId,
  voiceId,
  structuredCallId,
  miniMode = false,
  diallerMode = false,
  autoStart = false,
  shouldHangup = false,
  agentFirstMessage,
  onConnectionChange,
  onAudioLevelChange,
  onOutputLevelChange,
  onError,
  onHangupComplete,
}) => {
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
  const [error, setError] = useState<string | null>(null);
  const [agentInfoError, setAgentInfoError] = useState<string | null>(null);
  const [agentInfo, setAgentInfo] = useState<{
    agent_type: string;
    agent_name: string;
    agent_image_url?: string;
    whatsapp_number?: string;
  } | null>(null);
  const [conversationToken, setConversationToken] = useState<string | null>(
    null,
  );
  const [voiceProvider, setVoiceProvider] = useState<
    'elevenlabs' | 'openai' | null
  >(null);
  const [hasCallEnded, setHasCallEnded] = useState<boolean>(false);
  const [microphoneAccess, setMicrophoneAccess] = useState<boolean>(false);
  const [hasAttemptedMicrophone, setHasAttemptedMicrophone] =
    useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [isMicMuted, setIsMicMuted] = useState<boolean>(false);
  const [ellipsisCount, setEllipsisCount] = useState<number>(1);
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const threadIdRef = useRef<string | null>(null);
  const trackingIdRef = useRef<string | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const endConversationRef = useRef<() => Promise<void>>(() =>
    Promise.resolve(),
  );
  const isConnectedRef = useRef(false);
  const cancelledRef = useRef(false);

  // Track last context fetch to debounce
  const lastContextFetchRef = useRef<number>(0);
  const contextFetchDebounceMs = 500; // Don't fetch context more than once per 500ms

  // Ref to hold sendContextualUpdate function (set after conversation is initialized)
  const sendContextualUpdateRef = useRef<((text: string) => void) | null>(null);

  // Shared message handler for both providers
  const handleVoiceMessage = async (source: 'user' | 'ai', message: string) => {
    console.log(`[${voiceProvider}] message:`, { source, message });

    // Send message events to backend for conversation tracking
    if (source === 'ai') {
      sendEventToBackend(ElevenLabsEventType.AI_MESSAGE, {
        message: message,
        timestamp: Date.now(),
      });
    } else if (source === 'user') {
      sendEventToBackend(ElevenLabsEventType.USER_MESSAGE, {
        message: message,
        timestamp: Date.now(),
      });

      // Fetch and inject relevant memory context for user's message
      const now = Date.now();
      if (now - lastContextFetchRef.current >= contextFetchDebounceMs) {
        lastContextFetchRef.current = now;

        try {
          const headers: Record<string, string> = {};

          if (!callId && isAuthenticated) {
            const accessToken = await getAccessTokenSilently();
            headers.Authorization = `Bearer ${accessToken}`;
          }

          const response = await api.post<{
            context: string | null;
            reason?: string;
            stats?: {
              factCount: number;
              relationshipCount: number;
              guidanceCount: number;
              calendarEventCount: number;
            };
          }>(
            '/webrtc/token/context',
            {
              message: message,
              thread_id: threadIdRef.current,
              call_id: callId,
            },
            { headers },
          );

          if (response.data.context) {
            console.log(
              '[Context] Injecting context:',
              response.data.stats,
              'chars:',
              response.data.context.length,
            );
            sendContextualUpdateRef.current?.(response.data.context);
          } else {
            console.log('[Context] No context found:', response.data.reason);
          }
        } catch (error) {
          console.error('[Context] Failed to fetch context:', error);
        }
      }
    }
  };

  // Initialize ElevenLabs conversation
  const elevenLabsConversation = useConversation({
    micMuted: isMicMuted,
    onConnect: () => {
      console.log('ElevenLabs conversation connected');
      setHasCallEnded(false);
      setIsConnecting(false);
      setIsMicMuted(false);
      startAudioLevelMonitoring('elevenlabs');
      onConnectionChange?.(true);
    },
    onModeChange: ({ mode }) => {
      console.log(`ElevenLabs mode: ${mode}`);
    },
    onDisconnect: () => {
      console.log('ElevenLabs conversation disconnected');
      stopAudioLevelMonitoring();
      onConnectionChange?.(false);
    },
    onError: (error) => {
      console.error('ElevenLabs conversation error:', error);
      handleError(`Conversation error: ${error}`);
    },
    onMessage: async (message) => {
      if (message.source === 'ai' && message.message) {
        handleVoiceMessage('ai', message.message);
      } else if (message.source === 'user' && message.message) {
        handleVoiceMessage('user', message.message);
      }
    },
  });

  // Initialize OpenAI Realtime conversation
  const openaiConversation = useOpenAIRealtime({
    onConnect: () => {
      console.log('OpenAI Realtime conversation connected');
      setHasCallEnded(false);
      setIsConnecting(false);
      startAudioLevelMonitoring('openai');
      onConnectionChange?.(true);
    },
    onDisconnect: () => {
      console.log('OpenAI Realtime conversation disconnected');
      stopAudioLevelMonitoring();
      onConnectionChange?.(false);
    },
    onError: (error) => {
      console.error('OpenAI Realtime conversation error:', error);
      handleError(`Conversation error: ${error}`);
    },
    onMessage: async (message) => {
      handleVoiceMessage(message.source, message.message);
    },
    onSpeakingChange: (speaking) => {
      // OpenAI speaking state is handled internally
    },
  });

  // Unified conversation interface
  const conversation =
    voiceProvider === 'openai'
      ? {
          status:
            openaiConversation.status === 'connecting'
              ? 'connecting'
              : openaiConversation.status === 'connected'
                ? 'connected'
                : 'disconnected',
          isSpeaking: openaiConversation.isSpeaking,
          sendContextualUpdate: openaiConversation.sendContextualUpdate,
          getInputByteFrequencyData:
            openaiConversation.getInputByteFrequencyData,
          getOutputByteFrequencyData:
            openaiConversation.getOutputByteFrequencyData,
        }
      : {
          status: elevenLabsConversation.status,
          isSpeaking: elevenLabsConversation.isSpeaking,
          sendContextualUpdate: elevenLabsConversation.sendContextualUpdate,
          getInputByteFrequencyData:
            elevenLabsConversation.getInputByteFrequencyData,
          getOutputByteFrequencyData:
            elevenLabsConversation.getOutputByteFrequencyData,
        };

  // Set the sendContextualUpdate ref after conversation is available
  useEffect(() => {
    if (conversation.sendContextualUpdate) {
      sendContextualUpdateRef.current = conversation.sendContextualUpdate;
    }
  }, [conversation.sendContextualUpdate]);

  // Get parameters from props first, then fall back to URL params
  const urlParams = new URLSearchParams(window.location.search);
  const effectiveAgentId = agentId || urlParams.get('agent_id');
  const effectiveThreadId = threadId || urlParams.get('thread_id');
  const effectiveCallId = callId || urlParams.get('call_id');
  const effectiveVoiceId = voiceId || urlParams.get('voice_id');
  const shouldShowWhatsappButton =
    urlParams.get('redirect_to_whatsapp') === 'true';

  const handleError = (errorMessage: string) => {
    console.error('WebRTC Error:', errorMessage);
    setError(errorMessage);
    onError?.(errorMessage);
  };

  const startAudioLevelMonitoring = (provider: 'elevenlabs' | 'openai') => {
    const monitorAudioLevel = () => {
      try {
        const conv =
          provider === 'openai' ? openaiConversation : elevenLabsConversation;
        let inputLevel = 0;
        let outputLevel = 0;

        // Get input audio (user speaking)
        if (typeof conv.getInputByteFrequencyData === 'function') {
          const inputData = conv.getInputByteFrequencyData();
          if (inputData && inputData.length > 0) {
            const midRange = inputData.slice(10, 50);
            const average =
              midRange.reduce((sum: number, value: number) => sum + value, 0) /
              midRange.length;
            inputLevel = Math.min(average / 80, 1.5);
          }
        }

        // Get output audio (AI speaking)
        if (typeof conv.getOutputByteFrequencyData === 'function') {
          const outputData = conv.getOutputByteFrequencyData();
          if (outputData && outputData.length > 0) {
            const midRange = outputData.slice(10, 50);
            const average =
              midRange.reduce((sum: number, value: number) => sum + value, 0) /
              midRange.length;
            outputLevel = Math.min(average / 80, 1.5);
          }
        }

        // Use the maximum of input and output levels
        const maxLevel = Math.max(inputLevel, outputLevel);
        setAudioLevel(maxLevel);
        onAudioLevelChange?.(maxLevel);
        onOutputLevelChange?.(outputLevel);
      } catch (error) {
        console.log('Audio level monitoring not available:', error);
      }

      animationFrameRef.current = requestAnimationFrame(monitorAudioLevel);
    };

    animationFrameRef.current = requestAnimationFrame(monitorAudioLevel);
  };

  const stopAudioLevelMonitoring = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setAudioLevel(0);
    onAudioLevelChange?.(0);
    onOutputLevelChange?.(0);
  };

  const handleWhatsAppRedirect = () => {
    if (agentInfo?.whatsapp_number) {
      // Remove any non-numeric characters and format for WhatsApp
      const cleanNumber = agentInfo.whatsapp_number.replace(/\D/g, '');
      window.open(`https://wa.me/${cleanNumber}`, '_blank');
    }
  };

  const fetchAgentInfo = async () => {
    try {
      setAgentInfoError(null);

      const params: WebRTCChatVisualElementsParams = {};

      if (effectiveCallId) {
        params.call_id = effectiveCallId;
      } else if (effectiveAgentId && effectiveThreadId) {
        params.agent_id = effectiveAgentId;
        params.thread_id = effectiveThreadId;
      } else {
        // No valid parameters for agent info lookup
        return;
      }

      const response = await api.get<WebRTCChatVisualElementsResponse>(
        '/webrtc/chat-visual-elements',
        { params },
      );

      if (response.data.success && response.data.agent) {
        setAgentInfo(response.data.agent);
      } else {
        setAgentInfoError(
          response.data.error || 'Failed to fetch agent information',
        );
      }
    } catch (error) {
      console.error('Agent info fetch error:', error);
      setAgentInfoError('Unable to load agent information');
    }
  };

  // Token response type that supports both providers
  interface WebRTCTokenResponse {
    provider: 'elevenlabs' | 'openai';
    thread_id: string | null;
    agent_id: string;
    tracking_id?: string;
    // ElevenLabs specific
    token?: string;
    agentConfig?: any;
    // OpenAI specific
    openaiConfig?: OpenAIRealtimeConfig;
    openaiClientSecret?: string;
  }

  const getConversationToken = async (): Promise<WebRTCTokenResponse> => {
    try {
      let requestBody: ElevenLabsConversationTokenRequest;
      const headers: Record<string, string> = {};

      if (effectiveCallId) {
        // Unauthenticated mode with call_id
        requestBody = {
          call_id: effectiveCallId,
        };
      } else {
        // Authenticated mode with agent_id and optional thread_id
        const accessToken = await getAccessTokenSilently();
        requestBody = {
          agent_id: effectiveAgentId || undefined,
          ...(effectiveThreadId && { thread_id: effectiveThreadId }),
          ...(effectiveVoiceId && { voice_id: effectiveVoiceId }),
          ...(agentFirstMessage && {
            agent_first_message: agentFirstMessage,
          }),
          ...(structuredCallId && { structured_call_id: structuredCallId }),
        };
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const response = await api.post<WebRTCTokenResponse>(
        '/webrtc/token',
        requestBody,
        {
          headers,
        },
      );

      // Store thread ID and tracking ID from response
      if (response.data.thread_id) {
        threadIdRef.current = response.data.thread_id;
      }
      if (response.data.tracking_id) {
        trackingIdRef.current = response.data.tracking_id;
      }

      // Set the voice provider
      setVoiceProvider(response.data.provider);

      return response.data;
    } catch (error) {
      // Handle specific error responses from backend
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.data?.userMessage) {
          throw new Error(axiosError.response.data.userMessage);
        }
      }

      throw new Error(`Token generation failed: ${error}`);
    }
  };

  const startConversation = async () => {
    try {
      setError(null);
      setIsConnecting(true);
      cancelledRef.current = false;

      // Get conversation token and config (determines provider)
      const tokenData = await getConversationToken();
      if (cancelledRef.current) return;

      // Request microphone permission
      setHasAttemptedMicrophone(true);
      await navigator.mediaDevices.getUserMedia({ audio: true });
      if (cancelledRef.current) return;
      setMicrophoneAccess(true);

      if (tokenData.provider === 'openai') {
        // Start OpenAI Realtime conversation
        console.log('Starting OpenAI Realtime conversation');

        if (!tokenData.openaiConfig) {
          throw new Error('OpenAI config missing from token response');
        }

        if (!tokenData.openaiClientSecret) {
          throw new Error('OpenAI client secret missing from token response');
        }

        await openaiConversation.startSession({
          config: tokenData.openaiConfig,
          clientSecret: tokenData.openaiClientSecret,
        });

        if (cancelledRef.current) {
          await openaiConversation.endSession();
          return;
        }

        console.log(
          'OpenAI Realtime conversation started, tracking_id:',
          tokenData.tracking_id,
        );
      } else {
        // Start ElevenLabs conversation
        setConversationToken(tokenData.token || null);

        const configWithTracking = {
          ...tokenData.agentConfig,
        };

        console.log(
          'ElevenLabs overrides being passed:',
          JSON.stringify(configWithTracking, null, 2),
        );

        const conversationId = await elevenLabsConversation.startSession({
          conversationToken: tokenData.token!,
          connectionType: 'webrtc',
          overrides: configWithTracking,
          dynamicVariables: {
            tracking_id: tokenData.tracking_id || '',
          },
        });

        if (cancelledRef.current) {
          await elevenLabsConversation.endSession();
          return;
        }

        console.log(
          'ElevenLabs conversation started:',
          conversationId,
          'tracking_id:',
          tokenData.tracking_id,
        );
      }

      // Send session started event to backend
      sendEventToBackend(ElevenLabsEventType.CONVERSATION_STARTED, {
        timestamp: Date.now(),
      });
    } catch (error) {
      if (cancelledRef.current) return;
      console.error('Conversation start error:', error);
      setMicrophoneAccess(false);
      setIsConnecting(false);
      handleError(
        error instanceof Error ? error.message : 'Failed to start conversation',
      );
    }
  };

  const sendEventToBackend = async (
    eventType: ElevenLabsEventType,
    content: ElevenLabsEventContent,
  ) => {
    try {
      let requestBody: ElevenLabsConversationEventRequest;
      const headers: Record<string, string> = {};

      if (callId) {
        // Unauthenticated mode with call_id
        requestBody = {
          call_id: callId,
          event_type: eventType,
          content: content,
          device_type: getDeviceType(),
        };
      } else {
        // Authenticated mode with agent_id and thread_id
        const accessToken = await getAccessTokenSilently();
        requestBody = {
          thread_id: threadIdRef.current || '',
          agent_id: effectiveAgentId || undefined,
          event_type: eventType,
          content: content,
          device_type: getDeviceType(),
          ...(trackingIdRef.current
            ? { tracking_id: trackingIdRef.current }
            : {}),
        };
        headers.Authorization = `Bearer ${accessToken}`;
      }

      await api.post<ElevenLabsConversationEventResponse>(
        '/webrtc/events',
        requestBody,
        {
          headers,
        },
      );

      console.log(
        `Backend event sent: ${eventType} with ${callId ? `call_id: ${callId}` : `thread_id: ${threadIdRef.current || 'empty'}`}`,
      );
    } catch (error) {
      console.error(`Failed to send ${eventType} event:`, error);

      // Handle specific error responses from backend
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.data?.userMessage) {
          handleError(axiosError.response.data.userMessage);
          return;
        }
      }

      // Fallback error message
      handleError(`Failed to send ${eventType} event`);
    }
  };

  const endConversation = async () => {
    // Signal any in-flight startConversation() to abort
    cancelledRef.current = true;

    try {
      // Send call ended event to backend before disconnecting
      if (conversation.status === 'connected' && threadIdRef.current) {
        sendEventToBackend(ElevenLabsEventType.CONVERSATION_ENDED, {
          timestamp: Date.now(),
        });
      }

      setHasCallEnded(true);
      setIsConnecting(false);

      if (voiceProvider === 'openai') {
        await openaiConversation.endSession();
        console.log('OpenAI Realtime conversation ended');
      } else {
        await elevenLabsConversation.endSession();
        console.log('ElevenLabs conversation ended');
      }
    } catch (error) {
      console.error('Error ending conversation:', error);
    }
  };

  // Animated ellipsis for connecting state
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isConnecting) {
      interval = setInterval(() => {
        setEllipsisCount((prev) => (prev === 3 ? 1 : prev + 1));
      }, 500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isConnecting]);

  // Fetch agent info when component loads
  useEffect(() => {
    fetchAgentInfo();
  }, []);

  // Auto-start conversation if autoStart is true
  useEffect(() => {
    if (
      autoStart &&
      elevenLabsConversation.status === 'disconnected' &&
      openaiConversation.status === 'disconnected'
    ) {
      startConversation();
    }
  }, [autoStart]);

  // Handle hangup signal from parent
  useEffect(() => {
    const isConnected =
      voiceProvider === 'openai'
        ? openaiConversation.status === 'connected'
        : elevenLabsConversation.status === 'connected';

    if (shouldHangup && isConnected) {
      endConversation();
      onHangupComplete?.();
    }
  }, [
    shouldHangup,
    voiceProvider,
    openaiConversation.status,
    elevenLabsConversation.status,
    onHangupComplete,
  ]);

  // Keep refs in sync for cleanup
  useEffect(() => {
    endConversationRef.current = endConversation;
  });

  useEffect(() => {
    isConnectedRef.current = conversation.status === 'connected';
  }, [conversation.status]);

  // Escape key handler — hang up when connected
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isConnectedRef.current) {
        endConversationRef.current();
        onHangupComplete?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onHangupComplete]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isConnectedRef.current) {
        endConversationRef.current();
      }
    };
  }, []);

  // Get status text for mini mode
  const getStatusText = () => {
    if (isConnecting) return `Connecting${'.'.repeat(ellipsisCount)}`;
    if (error && conversation.status !== 'connected') return 'Call failed';
    if (conversation.status === 'connected') {
      return conversation.isSpeaking ? 'Speaking' : 'Listening';
    }
    return 'Start talking';
  };

  if (diallerMode) {
    const isFailed = !!error && conversation.status !== 'connected';
    const isConnected = conversation.status === 'connected';
    const isMuted = isConnected && isMicMuted;
    const isSpeaking = isConnected && conversation.isSpeaking;
    // First name / nickname only — never the agent's full name
    const nickname = (
      agentInfo?.agent_name ||
      agentInfo?.agent_type ||
      'Assistant'
    ).split(' ')[0];

    const stateClass = isFailed
      ? 'failed'
      : isMuted
        ? 'muted'
        : isSpeaking
          ? 'speaking'
          : isConnected
            ? 'listening'
            : 'calling';

    const label = isFailed
      ? "Couldn't connect"
      : isMuted
        ? 'Muted'
        : isSpeaking
          ? `${nickname} speaking`
          : isConnected
            ? 'Listening'
            : `Calling ${nickname}`;

    const handleDiallerHangup = async () => {
      await endConversation();
      onHangupComplete?.();
    };

    // Mic mute is only supported on the ElevenLabs provider
    const canMute = isConnected && voiceProvider !== 'openai';

    return (
      <div className='voice-dialler'>
        {agentInfo?.agent_image_url && (
          <div className='voice-dialler-avatar'>
            <img src={agentInfo.agent_image_url} alt={nickname} />
          </div>
        )}
        <div className={`voice-dialler-pill ${stateClass}`}>
          <div className='voice-dialler-pill-inner'>
            <button
              type='button'
              className='voice-dialler-state-icon'
              onClick={
                canMute ? () => setIsMicMuted((prev) => !prev) : undefined
              }
              disabled={!canMute}
              aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
              title={
                canMute
                  ? isMuted
                    ? 'Unmute microphone'
                    : 'Mute microphone'
                  : undefined
              }
            >
              {isFailed ? (
                <DiallerAlertIcon />
              ) : isMuted ? (
                <DiallerMicOffIcon />
              ) : (
                <DiallerMicIcon />
              )}
            </button>
            <span className='voice-dialler-label'>{label}</span>
            <button
              type='button'
              className='voice-dialler-hangup'
              onClick={handleDiallerHangup}
              aria-label='End call'
            >
              <DiallerCloseIcon />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (miniMode) {
    return (
      <div className='webrtc-voice-mini-wrapper'>
        {!isConnecting && (
          <AudioReactiveCanvas
            audioLevel={audioLevel}
            isSpeaking={conversation.isSpeaking || false}
            conversation={conversation}
            width={24}
            height={24}
            miniMode={true}
          />
        )}
        {isConnecting && <CallingIcon />}
        {error && conversation.status !== 'connected' && <CallFailedIcon />}
        <span className='status-text'>{getStatusText()}</span>
      </div>
    );
  }

  return (
    <div className='webrtc-voice-outer-wrapper'>
      <img
        src={agentInfo?.agent_image_url || ''}
        alt='Agent'
        className='direct-pro-image'
      />
      <div className='webrtc-voice-inner-wrapper'>
        <div className='topper'>
          <DreamLogo />
          <h2>
            Call with{' '}
            {agentInfo?.agent_name || agentInfo?.agent_type || 'Employee'}
          </h2>
        </div>
        <div className='action-center'>
          {error && (
            <div className='error-outer-wrapper'>
              <div className='error-inner-wrapper'>
                <CallFailedIcon />

                <div className='details-wrapper'>
                  <h2>
                    {conversation.status === 'connected'
                      ? 'Error in conversation'
                      : 'Voice call failed'}
                  </h2>
                  <p>{error}</p>
                </div>
                {conversation.status !== 'connected' ? (
                  <span onClick={startConversation}>Try again</span>
                ) : (
                  <div></div>
                )}
              </div>
            </div>
          )}
          {isConnecting && (
            <div className='connecting-status magic-box'>
              <CallingIcon />
              <span>Calling{'.'.repeat(ellipsisCount)}</span>
            </div>
          )}
          {!isConnecting &&
            !error &&
            conversation.status === 'disconnected' &&
            (!hasCallEnded ? (
              <button onClick={startConversation} className='new-call'>
                <StartCallIcon />
                Start Call
              </button>
            ) : (
              <div className='completed-information'>
                <CallCompletedIcon />
                <h2>Call completed</h2>
                <button onClick={startConversation} className='new-call'>
                  <StartCallIcon />
                  Start New Call
                </button>
                {shouldShowWhatsappButton && agentInfo?.whatsapp_number && (
                  <button
                    onClick={handleWhatsAppRedirect}
                    className='whatsapp-return'
                  >
                    <img src={WhatsappIcon} alt='Whatsapp' />
                    Back to Whatsapp
                  </button>
                )}
              </div>
            ))}
          {conversation.status === 'connected' && (
            <div className='call-in-progress-wrapper'>
              <div className='magic-box'>
                <div className='magic-box-call-inner'>
                  <p className='conn-state'>
                    <span className='green-dot'></span> Connected
                  </p>
                  <AudioReactiveCanvas
                    audioLevel={audioLevel}
                    isSpeaking={conversation.isSpeaking}
                    conversation={conversation}
                  />
                </div>
              </div>
              <div className='call-actions-line'>
                <button className='end-call' onClick={endConversation}>
                  <EndCallIcon />
                  End Call
                </button>
              </div>
            </div>
          )}
          {isConnecting && (
            <div className='call-in-progress-wrapper'>
              <div className='call-actions-line'>
                <button className='end-call' onClick={endConversation}>
                  <EndCallIcon />
                  End Call
                </button>
              </div>
            </div>
          )}
        </div>
        {hasAttemptedMicrophone && !hasCallEnded && (
          <div
            className={`
              ${microphoneAccess ? 'connected-line' : 'no-microphone-access'}
              connected-line
            `}
          >
            {microphoneAccess ? (
              <>
                <MicrophoneConnectedIcon />
                Microphone connected
              </>
            ) : (
              <>
                <NoMicrophoneIcon />
                No microphone detected
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebRTCVoiceChat;
